/**
 * Yennefer a Triss — „The Witcher: Realms Fall".
 *
 * RIVAL HEROES: na začiatku hry si hráč vyberie, kto z nich je hrdina; druhá
 * je sidekick. Hrdina má 14 zdravia, sidekick 6. Schopnosť má len hrdina:
 *
 *   • Yennefer (Sorceress of Vengerberg) — v kroku IMMEDIATELY, keď útočí
 *     Yennefer, smieš BOOSTOVAŤ útok. Zrušiť sa to nedá. S Triss nie.
 *   • Triss (Merigold the Fearless) — po vyriešení schémy, ktorú zahrala
 *     Triss, dostane 2 poškodenia susedná figúrka (povinné, aj vlastná).
 *     Keď schému zahrá Yennefer, nič.
 *
 * (Realms Fall, „Yennefer and Triss: Rival Heroes")
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  dealDamage,
  discardFromHand,
  drawCard,
  emit,
  gainAction,
  getMap,
  healFighter,
  heroOf,
  millDeck,
  returnFromDiscard,
} from '../ops.js';
import type { CardId, FighterId, PlayerIndex } from '../types.js';
import { cancel, emptySpacesInZone, fighterOrNull, opponentDiscards } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const YENNEFER = 'yennefer';
const TRISS = 'triss';
const HERO_HEALTH = 14;
const SIDEKICK_HEALTH = 6;
const ADVISOR = 'yennefer-triss.advisor-to-the-king';
const LODGE = 'yennefer-triss.lodge-of-sorceresses';

/** Kto je hrdinka hráča: `yennefer` alebo `triss`. */
function heroine(ctx: Ctx, player: PlayerIndex): string | undefined {
  return heroOf(ctx.state, player)?.defId;
}

function emptySpaces(ctx: Ctx): string[] {
  return getMap(ctx)
    .spaces.map((s) => s.id)
    .filter((id) => !ctx.state.fighters.some((f) => f.spaceId === id));
}

/** Figúrky v súboji, ktoré ešte stoja na doske. */
function combatants(ids: (FighterId | null)[], ctx: Ctx): FighterId[] {
  return ids.filter((id): id is FighterId => Boolean(id && ctx.state.fighters.some((f) => f.id === id)));
}

/** Voľba karty z vlastnej ruky, ktorá pôjde do odhodu (Incinerate, Telepathy). */
function askOwnDiscard(ctx: Ctx, player: PlayerIndex, source: CardId, label: string): void {
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label,
    optional: false,
    from: 'hand',
    purpose: 'effect',
    discards: true,
  });
}

/** Karta vybraná v `askOwnDiscard` ide do odhodu — hráč sa rozhodol sám, nie súperov efekt. */
const discardChosen = (ctx: Ctx, choice: { player: PlayerIndex }, cardId: CardId) => {
  discardFromHand(ctx, choice.player, cardId, 'effect');
};

/**
 * „This card's effects cannot be canceled. DURING COMBAT: Your opponent may
 * discard 2 cards to ignore this card's value."
 */
const incinerate: CardEffect = {
  uncancelable: true,
  duringChoice: (e) => {
    const opponent = e.opponentPlayer;
    if (opponent === null || (e.ctx.state.players[opponent]?.hand.length ?? 0) < 2) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: opponent,
      source: e.cardId,
      label: 'Incinerate: odhodíš 2 karty z ruky, aby sa hodnota útoku (7) ignorovala?',
      optional: true,
      effect: 'yennefer-triss.incinerate',
      yes: 'Odhodiť 2 karty',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    const combat = ctx.state.combat;
    if (!combat || !choice.source) return;
    (combat.flags ??= []).push('zeroAttack');
    askOwnDiscard(ctx, choice.player, choice.source, 'Incinerate: odhoď kartu (1 z 2)');
    askOwnDiscard(ctx, choice.player, choice.source, 'Incinerate: odhoď kartu (2 z 2)');
  },
  onCardChosen: discardChosen,
};

/**
 * „Your opponent chooses one: they discard 2 cards; deal 3 damage to their
 * hero; they discard the top 4 cards of their deck."
 */
const merigoldsHailstorm: CardEffect = {
  after: (e) => {
    const opponent = e.opponentPlayer;
    if (opponent === null) return;
    const follow = (data: string) => ({ kind: 'effect' as const, effect: 'yennefer-triss.merigolds-hailstorm', data });
    askChoice(e.ctx, {
      kind: 'option',
      player: opponent,
      source: e.cardId,
      label: "Merigold's Hailstorm: vyber si jedno",
      optional: false,
      options: [
        { label: 'Odhodiť 2 karty z ruky', follow: follow('discard') },
        { label: 'Tvoj hrdina dostane 3 poškodenia', follow: follow('damage') },
        { label: 'Odhodiť 4 vrchné karty balíčka', follow: follow('mill') },
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    const player = choice.player;
    if (data === 'damage') {
      const hero = heroOf(ctx.state, player);
      if (hero) dealDamage(ctx, hero.id, 3);
    } else if (data === 'mill') {
      millDeck(ctx, player, 4);
    } else {
      for (let i = 0; i < 2; i++) {
        askChoice(ctx, {
          kind: 'card',
          player,
          source: choice.source,
          label: `Merigold's Hailstorm: odhoď kartu (${i + 1} z 2)`,
          optional: false,
          from: 'hand',
          purpose: 'discard',
          discards: true,
        });
      }
    }
  },
};

/** „Place a fighter in the combat in any space. Gain 1 action." */
const portalToAnywhere: CardEffect = {
  after: (e) => {
    const ids = combatants([e.fighterId, e.opponentFighterId], e.ctx);
    if (ids.length === 1) {
      askPlaceAnywhere(e.ctx, e.player, e.cardId, ids[0]!);
    } else if (ids.length === 2) {
      askChoice(e.ctx, {
        kind: 'option',
        player: e.player,
        source: e.cardId,
        label: 'Portal to Anywhere: ktorú figúrku zo súboja premiestniš?',
        optional: false,
        options: ids.map((id) => ({
          label: fighterOrNull(e.ctx, id)?.name ?? id,
          follow: { kind: 'effect' as const, effect: 'yennefer-triss.portal-to-anywhere', data: id },
        })),
      });
    }
    gainAction(e.ctx, e.player);
  },
  onFollow: (ctx, choice, fighterId) => {
    if (fighterOrNull(ctx, fighterId)) askPlaceAnywhere(ctx, choice.player, choice.source, fighterId);
  },
};

function askPlaceAnywhere(ctx: Ctx, player: PlayerIndex, source: CardId | null, fighterId: FighterId): void {
  askChoice(ctx, {
    kind: 'place',
    player,
    source,
    label: `Portal to Anywhere: polož ${fighterOrNull(ctx, fighterId)?.name ?? 'figúrku'} na ľubovoľný priestor`,
    optional: false,
    fighterId,
    spaces: emptySpaces(ctx),
  });
}

/**
 * „If you won the combat, return this card to your hand." Karta v odhode je
 * až po upratovaní súboja — návrat preto ide ako automatické pokračovanie.
 */
const echoingBlast: CardEffect = {
  after: (e) => {
    if (!e.won) return;
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'Echoing Blast: karta späť do ruky',
      optional: false,
      follow: { kind: 'effect', effect: 'yennefer-triss.echoing-blast', data: e.cardId },
    });
  },
  onFollow: (ctx, choice, cardId) => {
    returnFromDiscard(ctx, choice.player, [cardId]);
  },
};

/** „If you won the combat, your opponent discards 1 card. Otherwise, both players discard 1 card." */
const ballLightning: CardEffect = {
  after: (e) => {
    if (!e.won && (e.ctx.state.players[e.player]?.hand.length ?? 0) > 0) {
      askChoice(e.ctx, {
        kind: 'card',
        player: e.player,
        source: e.cardId,
        label: 'Ball Lightning: odhoď 1 kartu',
        optional: false,
        from: 'hand',
        purpose: 'discard',
        discards: true,
      });
    }
    opponentDiscards(e, e.opponentPlayer);
  },
};

/**
 * „IMMEDIATELY: Your opponent may discard 1 card. If they don't, cancel all
 * effects on their card."
 */
const telepathy: CardEffect = {
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    const opponent = e.opponentPlayer;
    if (!combat || opponent === null) return;
    const theirs = e.role === 'attacker' ? combat.defenseCard : combat.attackCard;
    if (!theirs || combat.bonus) return;
    const side = e.role === 'attacker' ? 'defender' : 'attacker';
    if ((e.ctx.state.players[opponent]?.hand.length ?? 0) === 0) {
      cancel(e.ctx, side);
      return;
    }
    askChoice(e.ctx, {
      kind: 'confirm',
      player: opponent,
      source: e.cardId,
      label: 'Telepathy: odhodíš 1 kartu? Ak nie, efekty tvojej karty sa zrušia.',
      optional: true,
      effect: 'yennefer-triss.telepathy',
      yes: 'Odhodiť 1 kartu',
      no: 'Nie — zrušiť efekty',
      onSkip: { kind: 'effect', effect: 'yennefer-triss.telepathy', data: side },
    });
  },
  onConfirm: (ctx, choice) => {
    if (choice.source) askOwnDiscard(ctx, choice.player, choice.source, 'Telepathy: odhoď 1 kartu');
  },
  onCardChosen: discardChosen,
  onFollow: (ctx, _choice, side) => cancel(ctx, side === 'attacker' ? 'attacker' : 'defender'),
};


/** „Place your fighter in any space in their zone. Draw 1 card." */
const quickAndReady: CardEffect = {
  after: (e) => {
    if (e.fighterId && e.opponentFighterId) {
      askChoice(e.ctx, {
        kind: 'place',
        player: e.player,
        source: e.cardId,
        label: 'Quick and Ready: polož svoju figúrku na priestor v zóne súpera',
        optional: false,
        fighterId: e.fighterId,
        spaces: emptySpacesInZone(e.ctx, e.opponentFighterId),
      });
    }
    drawCard(e.ctx, e.player);
  },
};

/** „IMMEDIATELY: Your fighter recovers 1 health." */
const magicalBarrier: CardEffect = {
  immediate: (e) => {
    if (e.fighterId) healFighter(e.ctx, e.fighterId, 1);
  },
};

/** „IMMEDIATELY: The value of your opponent's card is equal to its printed value and cannot be changed." */
export const paralyzingFetters: CardEffect = {
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    if (!combat) return;
    (combat.flags ??= []).push(e.role === 'defender' ? 'printedAttack' : 'printedDefense');
  },
};

/**
 * „Each of your fighters recovers 1 health. Draw 1 card. ONGOING: Your BOOST
 * values are +1. Discard this card at the end of your turn if you didn't attack."
 */
const advisorToTheKing: CardEffect = {
  scheme: (e) => {
    const ids = e.ctx.state.fighters.filter((f) => f.owner === e.player).map((f) => f.id);
    for (const id of ids) healFighter(e.ctx, id, 1);
    drawCard(e.ctx, e.player);
  },
  ongoing: {
    discardAtEnd: (ctx) => !ctx.state.turn.actionsUsed.includes('attack'),
    boostBonus: 1,
  },
};

/**
 * „Each player simultaneously reveals a card from their hand and draws cards
 * equal to their card's BOOST value. Then, the player(s) who drew the most
 * deals that much damage to their hero."
 *
 * Súčasnosť: každý vyberá tajne (voľba ide do `state.reveal`, redakcia cudzie
 * skrýva) a odhalí sa, až keď vyberú všetci.
 */
const lodgeOfSorceresses: CardEffect = {
  scheme: (e) => {
    const count = e.ctx.state.players.length;
    const players = e.ctx.state.players.filter((p) => !p.eliminated);
    // Poradie volieb od hráča, ktorý kartu zahral — na výsledku nezáleží, voľby sú tajné.
    const seat = (index: PlayerIndex) => (index - e.player + count) % count;
    const ordered = [...players].sort((a, b) => seat(a.index) - seat(b.index));
    // Karta Lodge sa po vyriešení odhodí — do výberu nepatrí.
    const hand = (p: PlayerIndex) => (e.ctx.state.players[p]?.hand ?? []).filter((id) => id !== e.cardId);
    e.ctx.state.reveal = {
      source: e.cardId,
      player: e.player,
      picks: Object.fromEntries(ordered.filter((p) => hand(p.index).length === 0).map((p) => [p.index, null])),
    };
    for (const p of ordered) {
      if (hand(p.index).length === 0) continue;
      askChoice(e.ctx, {
        kind: 'card',
        player: p.index,
        source: e.cardId,
        label: 'Lodge of Sorceresses: tajne vyber kartu z ruky — potiahneš toľko, koľko je jej boost',
        optional: false,
        from: 'hand',
        purpose: 'effect',
        onSkip: { kind: 'effect', effect: LODGE, data: String(p.index) },
      });
    }
    finishLodge(e.ctx);
  },
  onCardChosen: (ctx, choice, cardId) => {
    if (!ctx.state.reveal) return;
    ctx.state.reveal.picks[choice.player] = cardId;
    finishLodge(ctx);
  },
  // Voľba prepadla (hráč medzitým nemá kartu) — ráta sa ako nič.
  onFollow: (ctx, _choice, player) => {
    if (!ctx.state.reveal) return;
    ctx.state.reveal.picks[Number(player)] = null;
    finishLodge(ctx);
  },
};

/** Keď vybrali všetci: odhalenie, ťahanie, poškodenie tomu, kto potiahol najviac. */
function finishLodge(ctx: Ctx): void {
  const reveal = ctx.state.reveal;
  if (!reveal) return;
  const active = ctx.state.players.filter((p) => !p.eliminated);
  if (active.some((p) => !(p.index in reveal.picks))) return;

  const drawn = new Map<PlayerIndex, number>();
  for (const p of active) {
    const cardId = reveal.picks[p.index];
    if (!cardId) {
      drawn.set(p.index, 0);
      continue;
    }
    emit(ctx, { type: 'cardRevealed', player: p.index, cardId });
    drawn.set(p.index, boostFor(ctx, p.index, cardId));
  }
  ctx.state.reveal = null;
  for (const [player, count] of drawn) {
    for (let i = 0; i < count; i++) drawCard(ctx, player);
  }
  const most = Math.max(0, ...drawn.values());
  if (most === 0) return;
  for (const [player, count] of drawn) {
    if (count !== most) continue;
    const hero = heroOf(ctx.state, player);
    if (hero) dealDamage(ctx, hero.id, most);
  }
}

/** Boost karty aj s Advisor to the King (boost hodnoty +1). */
function boostFor(ctx: Ctx, player: PlayerIndex, cardId: CardId): number {
  const def = ctx.content.cards[ctx.state.cards[cardId]!.defId];
  const ongoing = ctx.state.players[player]?.ongoing;
  const bonus = ongoing && ctx.state.cards[ongoing]?.defId === ADVISOR ? 1 : 0;
  return (def?.boost ?? 0) + bonus;
}

export const yenneferCards: EffectRegistry = {
  'yennefer-triss.incinerate': incinerate,
  'yennefer-triss.merigolds-hailstorm': merigoldsHailstorm,
  'yennefer-triss.portal-to-anywhere': portalToAnywhere,
  'yennefer-triss.echoing-blast': echoingBlast,
  'yennefer-triss.ball-lightning': ballLightning,
  'yennefer-triss.telepathy': telepathy,
  'yennefer-triss.quick-and-ready': quickAndReady,
  'yennefer-triss.magical-barrier': magicalBarrier,
  'yennefer-triss.paralyzing-fetters': paralyzingFetters,
  [ADVISOR]: advisorToTheKing,
  [LODGE]: lodgeOfSorceresses,
};

export const yenneferSpecial: HeroSpecial = {
  name: 'Rival Heroes',
  /** Na začiatku hry: kto bude hrdina? Pred prvou akciou, ako rozostavenie hmly. */
  setup: (ctx, player) => {
    const follow = (data: string) => ({ kind: 'special' as const, hero: YENNEFER, data });
    askChoice(ctx, {
      kind: 'option',
      player,
      source: null,
      label: 'Kto bude tvoj hrdina? (hrdina 14 zdravia, sidekick 6)',
      optional: false,
      options: [
        { label: 'Yennefer — smie boostovať svoj útok', follow: follow(YENNEFER) },
        { label: 'Triss — po svojej schéme 2 poškodenia susedovi', follow: follow(TRISS) },
      ],
    });
  },
  onFollow: (ctx, player, data) => {
    const yen = ctx.state.fighters.find((f) => f.owner === player && f.defId === YENNEFER);
    const triss = ctx.state.fighters.find((f) => f.owner === player && f.defId === TRISS);
    if (!yen || !triss) return;
    const [hero, sidekick] = data === TRISS ? [triss, yen] : [yen, triss];
    if (hero.kind !== 'hero') {
      // Hrdinka ide na štartový priestor hrdinu.
      const space = hero.spaceId;
      hero.spaceId = sidekick.spaceId;
      sidekick.spaceId = space;
      emit(ctx, { type: 'fightersSwapped', a: hero.id, b: sidekick.id });
    }
    // Rozostavenie sidekicka čaká vo fronte — týka sa teraz tej druhej.
    for (const choice of ctx.state.pending) {
      if (choice.kind === 'place' && choice.player === player && choice.fighterId === hero.id) {
        choice.fighterId = sidekick.id;
        choice.label = choice.label.replace(hero.name, sidekick.name);
      }
    }
    hero.kind = 'hero';
    hero.hp = hero.maxHp = HERO_HEALTH;
    sidekick.kind = 'sidekick';
    sidekick.hp = sidekick.maxHp = SIDEKICK_HEALTH;
    emit(ctx, { type: 'specialUsed', player, heroId: YENNEFER, name: `hrdinkou je ${hero.name}` });
  },
  /** Sorceress of Vengerberg — „If Yennefer is attacking, you may BOOST her attack." */
  attackImmediate: (ctx, player, fighterId) => {
    const fighter = fighterOrNull(ctx, fighterId);
    if (fighter?.defId !== YENNEFER || heroine(ctx, player) !== YENNEFER) return;
    if ((ctx.state.players[player]?.hand.length ?? 0) === 0) return;
    askChoice(ctx, {
      kind: 'card',
      player,
      source: null,
      label: 'Sorceress of Vengerberg: smieš boostovať útok Yennefer',
      optional: true,
      from: 'hand',
      purpose: 'boost',
      side: 'attacker',
    });
  },
  /** Merigold the Fearless — po schéme Triss 2 poškodenia susednej figúrke (povinné, aj vlastnej). */
  afterScheme: (ctx, player, fighterId) => {
    const fighter = fighterOrNull(ctx, fighterId);
    if (fighter?.defId !== TRISS || heroine(ctx, player) !== TRISS) return;
    const around = new Set(neighbors(getMap(ctx), fighter.spaceId));
    askChoice(ctx, {
      kind: 'damage',
      player,
      source: null,
      label: 'Merigold the Fearless: 2 poškodenia susednej figúrke',
      optional: false,
      amount: 2,
      targets: ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id),
    });
  },
};
